import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import Button from "../../Components/Button";
import {
  addShoppingItems,
  clearShoppingList,
  getShoppingList,
  removeShoppingItems,
  ShoppingItem,
} from "../../services/shoppingService";

/** Add and remove shopping list items, e.g. from a phone. */
export default function ShoppingListEditor() {
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<{ items: ShoppingItem[] }>) => {
    setBusy(true);
    setError(null);
    try {
      setItems((await action()).items);
    } catch (e: any) {
      setError(e?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    run(getShoppingList);
  }, []);

  const add = (e: React.FormEvent) => {
    e.preventDefault();
    // Commas let several items go in at once: "milk, eggs, bread".
    const newItems = text
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (newItems.length === 0) return;
    run(() => addShoppingItems(newItems)).then(() => setText(""));
  };

  const clearAll = () => {
    if (window.confirm("Clear the whole shopping list?")) {
      run(clearShoppingList);
    }
  };

  return (
    <div className="p-4" style={{ maxWidth: 560 }}>
      <Form onSubmit={add} className="d-flex gap-2 mb-3">
        <Form.Control
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add items, separated by commas"
          aria-label="New shopping items"
          disabled={busy}
        />
        <Button type="submit" className="primary-btn px-3" disabled={busy}>
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      </Form>

      {error && <div className="mb-3" style={{ color: "crimson" }}>{error}</div>}

      {items.length === 0 ? (
        <p className="text-muted">The list is empty.</p>
      ) : (
        <ul className="list-group mb-3">
          {items.map((item) => (
            <li
              key={item.id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              {item.text}
              <button
                type="button"
                className="btn btn-sm btn-link text-muted"
                onClick={() => run(() => removeShoppingItems([item.id]))}
                disabled={busy}
                aria-label={`Remove ${item.text}`}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <Button className="secondary-btn p-2" onClick={clearAll} disabled={busy}>
          Clear list
        </Button>
      )}
    </div>
  );
}
